import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Loader2, SlidersHorizontal } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const ALL = [["skill", "Coping skill"], ["breathe", "1-minute breathing"], ["supporter", "Lean on a supporter"]];
const LABEL = Object.fromEntries(ALL);

const StepRow = ({ id, on, i, last, onToggle, onMove }) => (
  <div className="flex items-center gap-2 py-2.5" data-testid={`plan-config-step-${id}`}>
    <Switch checked={on} onCheckedChange={onToggle} data-testid={`plan-config-toggle-${id}`} />
    <span className={`flex-1 text-sm font-medium ${on ? "" : "text-mm-muted"}`}>{LABEL[id]}</span>
    <button className="p-1.5 rounded-full hover:bg-mm-soft disabled:opacity-30" disabled={!on || i === 0} onClick={() => onMove(-1)} aria-label="Move up" data-testid={`plan-config-up-${id}`}><ArrowUp size={15} /></button>
    <button className="p-1.5 rounded-full hover:bg-mm-soft disabled:opacity-30" disabled={!on || last} onClick={() => onMove(1)} aria-label="Move down" data-testid={`plan-config-down-${id}`}><ArrowDown size={15} /></button>
  </div>
);

const Editor = ({ onClose }) => {
  const [cfg, setCfg] = useState(null);
  const [skills, setSkills] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/tough-day-plan/config").then((r) => setCfg(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/coping-skills").then((r) => setSkills(r.data)).catch(() => {});
  }, []);
  if (!cfg) return <Loader2 className="animate-spin mx-auto" />;
  const off = ALL.map(([id]) => id).filter((id) => !cfg.steps.includes(id));
  const toggle = (id, v) => setCfg((c) => ({ ...c, steps: v ? [...c.steps, id] : c.steps.filter((x) => x !== id) }));
  const move = (i, by) => setCfg((c) => { const s = [...c.steps]; [s[i], s[i + by]] = [s[i + by], s[i]]; return { ...c, steps: s }; });
  const save = async () => {
    if (!cfg.steps.length) return toast.error("Keep at least one step");
    setBusy(true);
    try { await api.put("/tough-day-plan/config", cfg); toast.success("Your plan is saved"); onClose(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <div className="divide-y">
        {cfg.steps.map((id, i) => <StepRow key={id} id={id} on i={i} last={i === cfg.steps.length - 1} onToggle={(v) => toggle(id, v)} onMove={(by) => move(i, by)} />)}
        {off.map((id) => <StepRow key={id} id={id} on={false} i={0} last onToggle={(v) => toggle(id, v)} onMove={() => {}} />)}
      </div>
      <p className="text-xs text-mm-muted">"Done, I'm ready" is always the last step.</p>
      <label className="text-sm font-medium">Coping skill for tough days
        <select className="mm-input mt-1.5 w-full" value={cfg.skill_id || ""} onChange={(e) => setCfg((c) => ({ ...c, skill_id: e.target.value || null }))} data-testid="plan-config-skill">
          <option value="">Automatic — what's helped me most</option>
          {skills.map((s) => <option key={s.id} value={s.id}>{s.icon} {s.name}</option>)}
        </select>
      </label>
      <button className="mm-btn w-full" disabled={busy} onClick={save} data-testid="plan-config-save">{busy ? <Loader2 size={16} className="animate-spin mx-auto" /> : "Save my plan"}</button>
    </>
  );
};

export const PlanCustomizer = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="mm-btn-ghost !py-1.5 !px-3 text-sm inline-flex items-center gap-1.5" onClick={() => setOpen(true)} data-testid="plan-customize-button"><SlidersHorizontal size={14} /> Customize my plan</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="plan-customize-dialog">
          <DialogHeader>
            <DialogTitle>Customize my plan</DialogTitle>
            <DialogDescription>Choose which steps appear on tough days and in what order.</DialogDescription>
          </DialogHeader>
          {open && <Editor onClose={() => setOpen(false)} />}
        </DialogContent>
      </Dialog>
    </>
  );
};
